import ReactSelect from "react-select";
import CreatableSelect from "react-select/creatable";

interface Opcao {
  value: string;
  label: string;
}

interface SelectBuscaProps {
  options: Opcao[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  isMulti?: false;
  // Casa com o htmlFor de um <Label> — react-select já suporta isso nativo
  // via inputId, só faltava expor.
  id?: string;
  // Spinner nativo do react-select — pra quando as opções ainda tão sendo buscadas.
  isLoading?: boolean;
  // Permite criar novos valores digitando no campo
  isCreatable?: boolean;
}

interface SelectBuscaMultiProps {
  options: Opcao[];
  value?: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  isMulti: true;
  id?: string;
  isLoading?: boolean;
  // Permite criar novos valores digitando no campo
  isCreatable?: boolean;
}

const classNames = {
  control: (state: any) =>
    `h-11 rounded-lg border bg-transparent px-1 text-sm dark:bg-gray-900 ${
      state.isFocused ? "border-brand-300 ring-3 ring-brand-500/10" : "border-gray-300 dark:border-gray-700"
    }`,
  placeholder: () => "px-2 text-gray-400",
  input: () => "px-2 text-gray-800 dark:text-white/90",
  singleValue: () => "px-2 text-gray-800 dark:text-white/90",
  valueContainer: () => "gap-1 px-1 py-0.5",
  multiValue: () => "gap-1 rounded-md bg-gray-100 pl-2 pr-1 py-0.5 dark:bg-white/10",
  multiValueLabel: () => "text-gray-700 dark:text-gray-200",
  multiValueRemove: () => "ml-1 rounded text-gray-400 hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-white",
  menu: () =>
    "mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900",
  menuList: () => "custom-scrollbar",
  option: (state: any) =>
    `cursor-pointer px-3 py-2 text-sm text-gray-700 dark:text-gray-200 ${
      state.isSelected ? "bg-brand-100 dark:bg-brand-500/20" : state.isFocused ? "bg-gray-50 dark:bg-white/5" : ""
    }`,
  noOptionsMessage: () => "px-3 py-2 text-sm text-gray-400",
  loadingIndicator: () => "text-gray-400 dark:text-gray-500",
};

// Select com busca (react-select) — usado onde a lista é grande demais pra
// rolar num <select> nativo (ex: município). Em português por padrão.
// isMulti=true muda value/onChange pra array (ex: várias lideranças num diretório).
export default function SelectBusca(props: SelectBuscaProps | SelectBuscaMultiProps) {
  const { options, placeholder = "Selecione...", isDisabled, isClearable = true, isCreatable = false } = props;
  const SelectComponent = isCreatable ? CreatableSelect : ReactSelect;

  if (props.isMulti) {
    // Encontra opções selecionadas, criando objetos temporários para valores customizados
    const selecionadas = (props.value || []).map(
      (val) => options.find((o) => o.value === val) || { value: val, label: val }
    );
    return (
      <SelectComponent
        inputId={props.id}
        isMulti
        unstyled
        classNames={classNames}
        options={options}
        value={selecionadas}
        onChange={(opcoes: any) => props.onChange((opcoes as readonly Opcao[]).map((o) => o.value))}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isClearable={isClearable}
        isLoading={props.isLoading}
        formatCreateLabel={(inputValue) => `Criar "${inputValue}"`}
        noOptionsMessage={() => "Nenhuma opção encontrada"}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 100000 }) }}
      />
    );
  }

  // Encontra a opção selecionada ou cria um objeto temporário se for valor customizado
  let selecionada = options.find((o) => o.value === props.value) || null;
  if (!selecionada && props.value) {
    const isNumero = /^\d+$/.test(props.value);
    const labelTexto = isNumero ? `Zona ${props.value}` : props.value;
    selecionada = { value: props.value, label: labelTexto };
  }

  return (
    <SelectComponent
      inputId={props.id}
      unstyled
      classNames={classNames}
      options={options}
      value={selecionada}
      onChange={(opcao: any) => {
        // CreatableSelect pode retornar novo objeto ao criar valor
        const valor = opcao?.value || "";
        props.onChange(valor);
      }}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isClearable={isClearable}
      isLoading={props.isLoading}
      formatCreateLabel={(inputValue) => `Criar "${inputValue}"`}
      noOptionsMessage={() => "Nenhuma opção encontrada"}
      menuPortalTarget={document.body}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 100000 }) }}
    />
  );
}
